import React, { useEffect, useState } from "react";
import { Button, Form, Input, Modal, Switch, Tag, message } from "antd";
import {
  createStockLocation,
  getStockLocations,
  updateStockLocation,
} from "../../apis/stockLocations";
import { useNotification } from "../../NotificationContext";

const { TextArea } = Input;

const toArray = (res: any): any[] => {
  if (Array.isArray(res)) return res;
  if (Array.isArray(res?.data)) return res.data;
  if (Array.isArray(res?.data?.data)) return res.data.data;
  return [];
};

const StockLocationsList: React.FC = () => {
  const { showNotification } = useNotification();
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState<any | null>(null);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchRows();
  }, []);

  const fetchRows = () => {
    setLoading(true);
    getStockLocations()
      .then((res: any) => {
        setRows(toArray(res));
      })
      .catch(() => showNotification("error", "Failed to fetch locations", "", "topRight"))
      .finally(() => setLoading(false));
  };

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ is_active: true, is_default: false });
    setIsOpen(true);
  };

  const openEdit = (row: any) => {
    setEditing(row);
    form.setFieldsValue({
      name: row.name,
      code: row.code,
      is_default: row.isDefault ?? row.is_default,
      is_active: row.isActive ?? row.is_active,
      notes: row.notes,
    });
    setIsOpen(true);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      if (editing?.id) {
        // Keep existing type; do not expose type in UI
        await updateStockLocation(editing.id, {
          ...values,
          type: editing.type || "terminal",
        });
        message.success("Location updated");
      } else {
        await createStockLocation({
          ...values,
          type: "terminal",
        });
        message.success("Location created");
      }
      setIsOpen(false);
      fetchRows();
    } catch (err: any) {
      if (err?.errorFields) return;
      message.error(
        err?.response?.data?.details?.message ||
          err?.response?.data?.message ||
          "Failed to save location"
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="sm:flex sm:items-center sm:justify-between">
        <div>
          <h1 className="text-base font-semibold leading-6 text-gray-900">Locations</h1>
          <p className="mt-1 text-sm text-gray-500">
            Clinic stock locations used in Stock Transfers (From / To). Main Pharmacy is the
            default; add wards, ICU, Housekeeping, etc. as needed.
          </p>
        </div>
        <button
          type="button"
          onClick={openCreate}
          className="mt-4 sm:mt-0 px-3 py-2 text-sm font-semibold text-white rounded-md shadow-sm bg-primary hover:bg-hover"
        >
          Add Location
        </button>
      </div>

      <div className="mt-8 overflow-hidden ring-1 ring-black ring-opacity-5 shadow sm:rounded-lg">
        <table className="min-w-full divide-y divide-gray-300">
          <thead className="bg-gray-50">
            <tr>
              <th className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                Name
              </th>
              <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Code</th>
              <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Status</th>
              <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {loading ? (
              <tr>
                <td colSpan={4} className="py-8 text-center text-gray-500">
                  Loading...
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={4} className="py-8 text-center text-gray-500">
                  No locations found. Add locations to use them in stock transfers.
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr key={row.id}>
                  <td className="py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                    {row.name}
                    {(row.isDefault ?? row.is_default) ? (
                      <Tag className="ml-2" color="green">
                        Default
                      </Tag>
                    ) : null}
                  </td>
                  <td className="px-3 py-4 text-sm text-gray-700">{row.code || "-"}</td>
                  <td className="px-3 py-4 text-sm">
                    {(row.isActive ?? row.is_active) === false ? (
                      <Tag color="red">Inactive</Tag>
                    ) : (
                      <Tag color="green">Active</Tag>
                    )}
                  </td>
                  <td className="px-3 py-4 text-sm">
                    <Button size="small" onClick={() => openEdit(row)}>
                      Edit
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Modal
        title={editing ? "Edit Location" : "Add Location"}
        open={isOpen}
        onCancel={() => setIsOpen(false)}
        onOk={handleSave}
        confirmLoading={saving}
        okText="Save"
      >
        <Form form={form} layout="vertical" className="mt-2">
          <Form.Item name="name" label="Name" rules={[{ required: true, message: "Name is required" }]}>
            <Input placeholder="e.g. ICU Ward Substore" />
          </Form.Item>
          <Form.Item name="code" label="Code">
            <Input placeholder="e.g. ICU" />
          </Form.Item>
          <Form.Item name="is_default" label="Default (Main Pharmacy)" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item name="is_active" label="Active" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item name="notes" label="Notes">
            <TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default StockLocationsList;
